import { ComponentProps, PropsWithChildren } from "react";
import { IoClose } from "react-icons/io5";

import { cn } from "@bentolabs/kit/utils";
import { Dialog as RadixDialog } from "radix-ui";

import { Button } from "./button";

export const Dialog = (props: RadixDialog.DialogProps) => {
  return <RadixDialog.Root {...props} />;
};

const DialogTrigger = ({
  className,
  ...props
}: RadixDialog.DialogTriggerProps) => {
  return <RadixDialog.Trigger {...props} asChild />;
};

const DialogTitle = ({
  className,
  children,
  showClose = false,
  ...props
}: RadixDialog.DialogTitleProps & { showClose?: boolean }) => {
  return (
    <RadixDialog.Title
      className={cn("flex items-center px-4.5 py-6.5 text-xl", className)}
      {...props}
    >
      {children}
      {showClose && (
        <RadixDialog.DialogClose asChild>
          <Button variant="nobel/10" className="ml-auto size-9.5 p-0">
            <IoClose className="size-6" />
          </Button>
        </RadixDialog.DialogClose>
      )}
    </RadixDialog.Title>
  );
};
const DialogFooter = ({
  className,
  children,
  ...props
}: ComponentProps<"footer">) => {
  return (
    <footer
      className={cn("flex flex-col overflow-hidden rounded-3xl p-4", className)}
      {...props}
    >
      {children}
    </footer>
  );
};

const Overlay = ({ className, ...props }: RadixDialog.DialogOverlayProps) => {
  return (
    <RadixDialog.Overlay
      className={cn(
        "data-[state=open]:animate-fade-in data-[state=closed]:animate-fade-out",
        "bg-onyx/85 fixed inset-0 z-40 backdrop-blur",
        className
      )}
      {...props}
    />
  );
};

type BodyProps = PropsWithChildren<{ className?: string }>;
const Body = ({ className, ...props }: BodyProps) => {
  return (
    <div
      style={{
        boxShadow:
          "0px 12px 34.5px 0px #0000004D, 0px 1px 1px 0px #FFFFFF1A inset",
      }}
      className={cn(
        "relative my-auto grid w-full gap-4 overflow-y-auto rounded-3xl",
        "bg-baltic-sea/30 outline-hidden backdrop-blur-[220px]",
        className
      )}
      {...props}
    />
  );
};

const DialogContent = ({
  className,
  children,
  ...props
}: RadixDialog.DialogContentProps) => {
  return (
    <RadixDialog.Portal>
      <Overlay />
      <RadixDialog.Content
        className={cn(
          "flex h-dvh flex-col overflow-y-hidden py-3 outline-hidden",
          "w-full max-w-xs md:max-w-lg",
          "fixed top-0 left-[50%] z-40 translate-x-[-50%]",
          "data-[state=open]:animate-fade-in data-[state=closed]:animate-fade-out",
          "duration-200",
          className
        )}
        {...props}
      >
        {children}
      </RadixDialog.Content>
    </RadixDialog.Portal>
  );
};

Dialog.Close = RadixDialog.Close;
Dialog.Body = Body;
Dialog.Overlay = Overlay;
Dialog.Portal = RadixDialog.Portal;
Dialog.Trigger = DialogTrigger;
Dialog.Description = RadixDialog.Description;
Dialog.Content = DialogContent;
Dialog.Title = DialogTitle;
Dialog.Footer = DialogFooter;
